import dayjs from 'dayjs';

import {
  IMarketingOfferCodes,
  MARKETING_OFFER_CODES,
} from '@src/constants/marketing.constants';
import { IUserSubscription } from '@src/interfaces/user.interfaces';

export const toFixedSubscriptionCostDivisor = (
  ...numbers: (number | undefined)[]
): string => {
  if (numbers.some((value) => value === undefined || value <= 0)) return '0.00';

  return (numbers as number[])
    .reduce((acc, value) => (acc > 0 ? acc / value : value), 0)
    .toFixed(2);
};

export type ISubsStatusOptions<T> = {
  isActive: T;
  isCancelled: NoInfer<T>;
  isPaused: NoInfer<T>;
  isExpired?: NoInfer<T>;
  default?: NoInfer<T>;
};

/**
 * Returns the option matching the current subscription status.
 * Priority: paused > cancelled > active.
 * Falls back to `default` (or undefined) when none applies.
 */
export function subscriptionStatusSwitchHelper<T>(
  data: IUserSubscription | undefined,
  options: ISubsStatusOptions<T> & { default: NoInfer<T> },
): T;
export function subscriptionStatusSwitchHelper<T>(
  data: IUserSubscription | undefined,
  options: ISubsStatusOptions<T>,
): T | undefined;
export function subscriptionStatusSwitchHelper<T>(
  data: IUserSubscription | undefined,
  options: ISubsStatusOptions<T>,
): T | undefined {
  const { is_active, is_canceled, is_paused, ends_at } = data || {};

  const isExpired = dayjs.utc().isAfter(ends_at);

  if (isExpired && !!options.isExpired) return options.isExpired;
  if (is_paused) return options.isPaused;
  if (is_canceled) return options.isCancelled;
  if (is_active) return options.isActive;

  return options.default;
}

export const isMarketingOfferCode = (
  value: unknown,
): value is IMarketingOfferCodes =>
  Object.values(MARKETING_OFFER_CODES).includes(value as IMarketingOfferCodes);
